/*
 * Site search — Chaandbali
 * Markup: snippets/site-search.liquid. Styles: assets/site-search.css.
 *
 * A floating search box. Motion follows the iOS playbook:
 *   - the box grows out of whatever opened it and shrinks back into it
 *   - its height glides when the content changes (no jumps)
 *   - one highlight glides between options (pointer and arrow keys)
 *   - on touch, the box can be pulled down to dismiss, with rubber-banding
 *
 * Focus stays in the field the whole time (combobox pattern): arrow keys
 * move the active option, Enter opens it, Esc closes. Results come from
 * Shopify's predictive search; stale requests are aborted.
 */
(() => {
  if (customElements.get('site-search')) return;

  const RECENT_KEY = 'sx-recent';
  const RECENT_MAX = 5;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const ICON_SEARCH =
    '<svg class="sx-row__icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><circle cx="10.75" cy="10.75" r="6.25"/><path d="M15.35 15.35 20 20"/></svg>';
  const ICON_CLOCK =
    '<svg class="sx-row__icon" aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"><circle cx="8" cy="8" r="6"/><path d="M8 4.8V8l2 1.4"/></svg>';
  const ICON_GO =
    '<svg class="sx-row__go" aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><path d="M2 8h11M9 4l4 4-4 4"/></svg>';

  const escapeHtml = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const highlight = (text, query) => {
    const safe = escapeHtml(text);
    const q = query.trim();
    if (!q) return safe;
    return safe.replace(new RegExp(`(${escapeRegExp(escapeHtml(q))})`, 'ig'), '<mark>$1</mark>');
  };

  const recentStore = {
    read() {
      try {
        return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
      } catch (e) {
        return [];
      }
    },
    write(list) {
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(list));
      } catch (e) {}
    },
  };

  let optionId = 0;

  class SiteSearch extends HTMLElement {
    connectedCallback() {
      // Live on <body>, outside the (transformed) sticky header.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';

      this.box = this.querySelector('.sx__box');
      this.form = this.querySelector('.sx__bar');
      this.input = this.querySelector('.sx__input');
      this.clearButton = this.querySelector('.sx__clear');
      this.body = this.querySelector('.sx__body');
      this.footer = this.querySelector('[data-sx-all]');
      this.status = this.querySelector('[data-sx-status]');
      this.views = {
        idle: this.querySelector('[data-sx-view="idle"]'),
        results: this.querySelector('[data-sx-view="results"]'),
      };

      this.glow = document.createElement('div');
      this.glow.className = 'sx__highlight';
      this.glow.setAttribute('aria-hidden', 'true');
      this.body.prepend(this.glow);

      try {
        this.money = new Intl.NumberFormat(this.dataset.locale || 'en', {
          style: 'currency',
          currency: this.dataset.currency || 'AUD',
          currencyDisplay: 'narrowSymbol',
        });
      } catch (e) {
        this.money = new Intl.NumberFormat('en', { style: 'currency', currency: 'AUD' });
      }

      // Empty state offers the browse pills.
      const browse = this.views.idle.querySelector('.sx__pills');
      const empty = this.querySelector('[data-sx-empty]');
      if (browse && empty) empty.appendChild(browse.cloneNode(true));

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeyDown);
      this.clearButton.addEventListener('click', this.onClear);
      this.form.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', (e) => e.key === 'Tab' && this.trapTab(e));
      this.body.addEventListener('pointermove', this.onPointerMove);
      this.body.addEventListener('pointerleave', () => this.setActive(-1));
      this.footer.addEventListener('pointerenter', () => this.setActive(this.options().indexOf(this.footer)));
      this.bindDrag();
      this.bindTriggers();
    }

    /* ---------------- triggers ---------------- */

    bindTriggers() {
      document.addEventListener(
        'click',
        (event) => {
          const trigger = event.target.closest('.header__icon--search, .menu-drawer .cb-search__input');
          if (!trigger) return;
          event.preventDefault();
          event.stopPropagation();
          this.open(trigger);
        },
        true
      );
      document.addEventListener(
        'focusin',
        (event) => {
          if (event.target.matches('.menu-drawer .cb-search__input')) {
            event.target.blur();
            this.open(event.target);
          }
        },
        true
      );
      document.addEventListener('keydown', (event) => {
        if (event.key !== '/' || this.isOpen) return;
        if (event.target.closest('input, textarea, select, [contenteditable]')) return;
        event.preventDefault();
        this.open();
      });
    }

    /* ---------------- open / close ---------------- */

    get isOpen() {
      return this.classList.contains('is-open');
    }

    // Point the box's "collapsed" transform at the element that opened it,
    // so it grows out of (and back into) the search icon.
    aimAt(origin) {
      const rect = origin && origin.getBoundingClientRect ? origin.getBoundingClientRect() : null;
      if (!rect || !rect.width || reduceMotion.matches) {
        this.box.style.setProperty('--sx-from-x', '0px');
        this.box.style.setProperty('--sx-from-y', '-12px');
        this.box.style.setProperty('--sx-from-scale', '0.96');
        return;
      }
      const top = parseFloat(getComputedStyle(this.box).top) || 0;
      const x = rect.left + rect.width / 2 - window.innerWidth / 2;
      const y = rect.top + rect.height / 2 - top - 20;
      this.box.style.setProperty('--sx-from-x', `${Math.round(x)}px`);
      this.box.style.setProperty('--sx-from-y', `${Math.round(y)}px`);
      this.box.style.setProperty('--sx-from-scale', '0.2');
    }

    open(trigger) {
      if (this.isOpen) return;
      this.origin = trigger || null;
      clearTimeout(this.hideTimer);
      this.hidden = false;
      this.box.style.setProperty('--sx-drag', '0px');
      this.aimAt(this.origin);
      document.documentElement.classList.add('sx-open');
      this.renderRecent();
      this.showView(this.input.value.trim() ? 'results' : 'idle', false);
      // Focus inside the tap so iOS raises the keyboard straight away.
      this.input.focus({ preventScroll: true });
      void this.box.offsetWidth;
      this.classList.add('is-open');
      this.body.style.height = '';
    }

    close() {
      if (!this.isOpen) return;
      this.aimAt(this.origin);
      this.classList.remove('is-open', 'is-loading', 'is-dragging');
      document.documentElement.classList.remove('sx-open');
      if (this.controller) this.controller.abort();
      this.setActive(-1);
      this.hideTimer = setTimeout(() => (this.hidden = true), reduceMotion.matches ? 0 : 450);
      const back = this.origin;
      if (back && back.focus && !back.matches('.cb-search__input')) back.focus({ preventScroll: true });
    }

    /* ---------------- height morph ---------------- */

    // Run `change`, then glide the body from its old height to its new one.
    morph(change) {
      if (reduceMotion.matches || !this.isOpen) {
        change();
        return;
      }
      const from = this.body.getBoundingClientRect().height;
      this.body.style.height = '';
      change();
      const to = this.body.getBoundingClientRect().height;
      if (Math.abs(to - from) < 2) return;
      this.body.style.height = `${from}px`;
      void this.body.offsetHeight;
      this.body.style.height = `${to}px`;
      clearTimeout(this.morphTimer);
      this.morphTimer = setTimeout(() => (this.body.style.height = ''), 520);
    }

    showView(name, animate = true) {
      const current = this.views.results.hidden ? 'idle' : 'results';
      const apply = () => {
        Object.entries(this.views).forEach(([key, el]) => (el.hidden = key !== name));
        this.footer.hidden = name !== 'results' || !this.input.value.trim();
        if (animate && current !== name) {
          const view = this.views[name];
          view.classList.remove('is-entering');
          void view.offsetWidth;
          view.classList.add('is-entering');
        }
      };
      if (animate) this.morph(apply);
      else apply();
      this.setActive(-1);
    }

    /* ---------------- options + gliding highlight ---------------- */

    options() {
      const view = this.views.results.hidden ? this.views.idle : this.views.results;
      const list = [...view.querySelectorAll('[data-sx-option]')].filter((el) => el.offsetParent !== null);
      if (!this.footer.hidden) list.push(this.footer);
      list.forEach((el) => {
        if (!el.id) el.id = `sx-option-${++optionId}`;
      });
      return list;
    }

    setActive(index) {
      const options = this.options();
      if (this.active) this.active.classList.remove('is-active');
      this.active = options[index] || null;
      if (!this.active) {
        this.input.removeAttribute('aria-activedescendant');
        this.glow.classList.remove('is-visible');
        return;
      }
      this.active.classList.add('is-active');
      this.input.setAttribute('aria-activedescendant', this.active.id);
      if (this.active.classList.contains('sx-row')) this.moveGlow(this.active);
      else this.glow.classList.remove('is-visible');
      if (this.body.contains(this.active)) this.active.scrollIntoView({ block: 'nearest' });
    }

    moveGlow(target) {
      const bodyRect = this.body.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      const x = rect.left - bodyRect.left;
      const y = rect.top - bodyRect.top + this.body.scrollTop;
      const wasVisible = this.glow.classList.contains('is-visible');
      if (!wasVisible) this.glow.style.transition = 'none';
      this.glow.style.transform = `translate(${x}px, ${y}px)`;
      this.glow.style.width = `${rect.width}px`;
      this.glow.style.height = `${rect.height}px`;
      if (!wasVisible) {
        void this.glow.offsetWidth;
        this.glow.style.transition = '';
      }
      this.glow.classList.add('is-visible');
    }

    onPointerMove = (event) => {
      if (event.pointerType === 'touch') return;
      const option = event.target.closest('[data-sx-option]');
      if (!option || option === this.active) return;
      this.setActive(this.options().indexOf(option));
    };

    /* ---------------- input ---------------- */

    onInput = () => {
      const query = this.input.value;
      this.clearButton.hidden = query.length === 0;
      clearTimeout(this.debounce);
      if (!query.trim()) {
        if (this.controller) this.controller.abort();
        this.classList.remove('is-loading');
        this.showView('idle');
        return;
      }
      this.classList.add('is-loading');
      this.setQueryText(query);
      this.debounce = setTimeout(() => this.fetchResults(query), 160);
    };

    onClear = () => {
      this.input.value = '';
      this.onInput();
      this.input.focus();
    };

    onSubmit = (event) => {
      const query = this.input.value.trim();
      if (!query) {
        event.preventDefault();
        return;
      }
      this.remember(query);
    };

    onKeyDown = (event) => {
      const options = this.options();
      const index = this.active ? options.indexOf(this.active) : -1;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.setActive(Math.min(index + 1, options.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setActive(index - 1);
          break;
        case 'Enter':
          if (this.active) {
            event.preventDefault();
            this.active.click();
          }
          break;
        case 'Escape':
          event.preventDefault();
          if (this.input.value) this.onClear();
          else this.close();
          break;
        default:
      }
    };

    trapTab(event) {
      const focusables = [...this.box.querySelectorAll('input:not([type=hidden]), button:not([hidden]), a[href]')].filter(
        (el) => el.offsetParent !== null
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onClick = (event) => {
      if (event.target.closest('[data-sx-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.target.closest('[data-sx-clear-recent]')) {
        recentStore.write([]);
        this.morph(() => this.renderRecent());
        this.input.focus();
        return;
      }
      const recent = event.target.closest('[data-sx-recent-term]');
      if (recent) {
        event.preventDefault();
        this.input.value = recent.dataset.sxRecentTerm;
        this.onInput();
        this.input.focus();
        return;
      }
      const term = event.target.closest('[data-sx-term]');
      if (term) this.remember(term.dataset.sxTerm);
      else if (event.target.closest('a[data-sx-option]') && this.input.value.trim()) this.remember(this.input.value.trim());
    };

    /* ---------------- drag to dismiss (touch) ---------------- */

    bindDrag() {
      let start = null;
      this.form.addEventListener(
        'touchstart',
        (event) => {
          if (event.target.closest('input, button')) return;
          const t = event.touches[0];
          start = { y: t.clientY, time: performance.now(), last: t.clientY, v: 0 };
          this.classList.add('is-dragging');
        },
        { passive: true }
      );
      this.form.addEventListener(
        'touchmove',
        (event) => {
          if (!start) return;
          const t = event.touches[0];
          const raw = t.clientY - start.y;
          const now = performance.now();
          start.v = (t.clientY - start.last) / Math.max(1, now - start.time);
          start.last = t.clientY;
          start.time = now;
          // Follow the finger downward with resistance; barely give upward.
          const dy = raw > 0 ? raw * 0.6 : -Math.sqrt(-raw) * 1.5;
          this.box.style.setProperty('--sx-drag', `${dy}px`);
        },
        { passive: true }
      );
      const end = () => {
        if (!start) return;
        const dragged = parseFloat(this.box.style.getPropertyValue('--sx-drag')) || 0;
        const fling = start.v > 0.5 && dragged > 16;
        start = null;
        this.classList.remove('is-dragging');
        if (dragged > 80 || fling) this.close();
        else this.box.style.setProperty('--sx-drag', '0px');
      };
      this.form.addEventListener('touchend', end);
      this.form.addEventListener('touchcancel', end);
    }

    /* ---------------- data ---------------- */

    async fetchResults(query) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const response = await fetch(`${this.dataset.suggestUrl}.json?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const data = await response.json();
        if (this.input.value !== query) return;
        this.renderResults(query, (data.resources && data.resources.results) || {});
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderResults(query, {});
      } finally {
        if (this.input.value === query) this.classList.remove('is-loading');
      }
    }

    remember(query) {
      const q = query.trim();
      if (!q) return;
      const list = recentStore.read().filter((item) => item.toLowerCase() !== q.toLowerCase());
      list.unshift(q);
      recentStore.write(list.slice(0, RECENT_MAX));
    }

    /* ---------------- rendering ---------------- */

    setQueryText(query) {
      this.querySelectorAll('[data-sx-query]').forEach((el) => (el.textContent = query.trim()));
      this.footer.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(query.trim())}&options%5Bprefix%5D=last`;
    }

    renderRecent() {
      const section = this.querySelector('[data-sx-recent-section]');
      const list = this.querySelector('[data-sx-recent]');
      const recent = recentStore.read();
      section.hidden = recent.length === 0;
      list.innerHTML = recent
        .map(
          (q) =>
            `<li><button type="button" class="sx-row" role="option" data-sx-option data-sx-recent-term="${escapeHtml(q)}">${ICON_CLOCK}<span class="sx-row__text">${escapeHtml(q)}</span>${ICON_GO}</button></li>`
        )
        .join('');
    }

    renderResults(query, results) {
      const products = results.products || [];
      const collections = results.collections || [];
      const queries = (results.queries || []).filter((q) => q.text && q.text.toLowerCase() !== query.trim().toLowerCase());
      const nothing = !products.length && !collections.length && !queries.length;

      const fill = () => {
        this.querySelector('[data-sx-terms-section]').hidden = queries.length === 0;
        this.querySelector('[data-sx-terms]').innerHTML = queries
          .slice(0, 3)
          .map(
            (q) =>
              `<li><a class="sx-row" href="${escapeHtml(q.url)}" role="option" data-sx-option data-sx-term="${escapeHtml(q.text)}">${ICON_SEARCH}<span class="sx-row__text">${highlight(q.text, query)}</span>${ICON_GO}</a></li>`
          )
          .join('');

        this.querySelector('[data-sx-products-section]').hidden = products.length === 0;
        this.querySelector('[data-sx-products]').innerHTML = products.map((p) => this.productRow(p, query)).join('');

        this.querySelector('[data-sx-collections-section]').hidden = collections.length === 0;
        this.querySelector('[data-sx-collections]').innerHTML = collections
          .slice(0, 5)
          .map((c) => `<li><a class="sx__pill" href="${escapeHtml(c.url)}" role="option" data-sx-option>${escapeHtml(c.title)}</a></li>`)
          .join('');

        this.querySelector('[data-sx-empty]').hidden = !nothing;
      };

      if (this.views.results.hidden) {
        fill();
        this.showView('results');
      } else {
        this.morph(fill);
        this.footer.hidden = false;
      }
      this.setActive(-1);
      this.status.textContent = nothing ? `No results for ${query}` : `${products.length} products found`;
    }

    productRow(p, query) {
      const image = (p.featured_image && p.featured_image.url) || p.image || '';
      const src = image ? `${image}${image.includes('?') ? '&' : '?'}width=120` : '';
      const min = Number.parseFloat(p.price_min || p.price || 0);
      const max = Number.parseFloat(p.price_max || p.price || 0);
      const price = Number.isFinite(min) ? `${max > min ? 'From ' : ''}${this.money.format(min)}` : '';
      return `<li><a class="sx-row sx-row--product" href="${escapeHtml(p.url)}" role="option" data-sx-option>
        <span class="sx-row__thumb">${src ? `<img src="${escapeHtml(src)}" alt="" loading="lazy" width="40" height="50">` : ''}</span>
        <span class="sx-row__main">
          <span class="sx-row__title">${highlight(p.title, query)}</span>
          <span class="sx-row__meta">${escapeHtml(price)}${p.available === false ? '<span class="sx-row__tag">Sold out</span>' : ''}</span>
        </span>
        ${ICON_GO}
      </a></li>`;
    }
  }

  customElements.define('site-search', SiteSearch);
})();
